import { useAdminPresence } from '@/hooks/useAdminPresence';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Wifi, Clock, MapPin } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export function OnlineAdminsIndicator() {
  const { onlineAdmins, loading } = useAdminPresence();

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse" />
        Chargement...
      </div>
    );
  }

  return (
    <Card className="border-green-200 bg-green-50/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <div className="relative">
            <Wifi className="h-4 w-4 text-green-600" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          </div>
          Utilisateurs connectés ({onlineAdmins.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {onlineAdmins.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun utilisateur en ligne</p>
        ) : (
          <div className="space-y-2">
            {onlineAdmins.map((admin) => (
              <div 
                key={admin.user_id} 
                className="flex items-center justify-between p-2 bg-white rounded-lg border"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {admin.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{admin.full_name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline" className="text-xs px-1 py-0">
                        {admin.system_role === 'super_admin' ? '👑 Super Admin' : admin.system_role === 'admin' ? '🛡️ Admin' : '🏪 Vendeur'}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {admin.last_activity ? 
                      formatDistanceToNow(new Date(admin.last_activity), { 
                        addSuffix: true, 
                        locale: fr 
                      })
                      : 'Maintenant'
                    }
                  </div>
                  {admin.ip_address && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {admin.ip_address}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Petit indicateur pour la liste des utilisateurs
export function OnlineIndicator({ isOnline }: { isOnline: boolean }) {
  if (!isOnline) return null;
  
  return (
    <span 
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium"
      title="En ligne"
    >
      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
      En ligne
    </span>
  );
}